import React, { useEffect, useRef } from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { useTranslation } from '@lib/useTypedTranslation';

import { drawXAxisLabelsFromDateRange } from '../../../../components/chart/lib';
import { StackedColumnsDateChart } from '../../../../components/chart/stackedColumnsDateChart';
import { formatTooltipDay } from '../../../../components/chart/tooltips';
import { IDeviceEventCount } from '../../../../services/core/performance';
import { getDailyKeyRange, ProcessCrashesScope } from './useProcessCrashesScope';

type ChartData = { count: number, date: number };

// The selected day's own bar is faded to mark it, as the examined day's is on the anomaly charts, while every other bar keeps its
// colour. This follows deviceAnomaliesEventChart.tsx, whose comments give the reasons for the details below. As for the chart
// plumbing of that area's other charts, it is a self-contained copy rather than something shared.
export const SELECTED_COLUMN_OPACITY = 0.35;
export const DEFAULT_COLUMN_OPACITY = 1;

// The chart has a single series, so a day has at most one column
function findColumnForDate(chart: am4charts.XYChart, date: number): am4charts.Column | undefined {
  let found: am4charts.Column | undefined;
  chart.series.each((series) => {
    (series as am4charts.ColumnSeries).columns.each((column) => {
      const dataContext = column.dataItem?.dataContext as ChartData | undefined;
      if (dataContext && Number(dataContext.date) === date) {
        found = column;
      }
    });
  });
  return found;
}

// The opacity is set on the default state of the column, not just on the column. addHoverAnimation (shared by the bar charts)
// resets every column to its default state whenever the pointer leaves a bar, which would undo an opacity set only on the column.
function setColumnOpacity(column: am4charts.Column, opacity: number) {
  column.defaultState.properties.fillOpacity = opacity;
  column.setState('default');
}

// Fades the bar of the selected day, after restoring that of the day selected before. Exported so that its guard for the chart not
// being available yet can be tested directly: a render can't reach it, as the chart is created (and so captured by drawXAxis)
// before the effect which calls this runs.
export function applySelectedDateHighlight(
  chart: am4charts.XYChart | null,
  highlightedColumnRef: React.MutableRefObject<am4charts.Column | null>,
  selectedDate: number | undefined
) {
  if (!chart) {
    return;
  }
  if (highlightedColumnRef.current) {
    setColumnOpacity(highlightedColumnRef.current, DEFAULT_COLUMN_OPACITY);
    highlightedColumnRef.current = null;
  }
  if (selectedDate) {
    const column = findColumnForDate(chart, selectedDate);
    if (column) {
      setColumnOpacity(column, SELECTED_COLUMN_OPACITY);
      highlightedColumnRef.current = column;
    }
  }
}

interface IProps {
  data: IDeviceEventCount[],
  scope: ProcessCrashesScope,
  filename: string,
  onExportClicked: () => Promise<string>,
  /** Called with the local calendar day ('YYYY-MM-DD') of a clicked column */
  onDayClick: (day: string) => void,
  /** The local calendar day ('YYYY-MM-DD') selected from the chart, whose bar is faded */
  selectedDay?: string
}

export function ProcessCrashesChart(props: IProps) {
  const { data, scope, filename, onExportClicked, onDayClick, selectedDay } = props;
  const { t } = useTranslation('coreProcesses');
  const dateRange = getDailyKeyRange(scope, scope.timeZone);
  // The columns are keyed by the date of their local day at UTC midnight, as the days of the API are
  const selectedDate = selectedDay ? moment.utc(selectedDay).valueOf() : undefined;

  // drawXAxis, which is how the chart is reached, only runs when the chart is created. Selecting a day doesn't change the data, so
  // doesn't create another chart, and this effect moves the highlight to the day selected, on that same chart.
  const chartRef = useRef<am4charts.XYChart | null>(null);
  const highlightedColumnRef = useRef<am4charts.Column | null>(null);

  useEffect(() => {
    const chart = chartRef.current;
    applySelectedDateHighlight(chart, highlightedColumnRef, selectedDate);
    // amCharts creates the columns of each series asynchronously, so they may not exist yet when this first runs. The chart can
    // report that it has validated (and so is ready) while a series is still creating its columns, so the highlight is applied
    // again once the chart, and each of its series, validates (see deviceAnomaliesEventChart.tsx).
    const disposer = chart?.events.on('validated', () => {
      applySelectedDateHighlight(chart, highlightedColumnRef, selectedDate);
    });
    const seriesDisposers: { dispose: () => void }[] = [];
    chart?.series.each((series) => {
      seriesDisposers.push(series.events.on('validated', () => {
        applySelectedDateHighlight(chart, highlightedColumnRef, selectedDate);
      }));
    });
    return () => {
      disposer?.dispose();
      seriesDisposers.forEach((seriesDisposer) => seriesDisposer.dispose());
    };
  }, [selectedDate]);

  // The daily events API identifies each local calendar day by that date at UTC midnight, so the clicked column's date
  // is the local calendar day as it stands, with no time zone conversion. Zero count days can be selected too.
  const onBarClick = (dataContext: ChartData) => {
    if (!dataContext || !Number.isFinite(dataContext.date)) {
      return;
    }
    onDayClick(moment.utc(dataContext.date).format('YYYY-MM-DD'));
  };

  return (
    <StackedColumnsDateChart<ChartData>
      cypressId="processCrashesChart"
      data={data.map(({ count, date }) => ({ count, date: parseInt(date) }))}
      title={t('PROCESS_CRASHES_PER_DAY')}
      days={scope.days}
      series={[{
        dataKey: 'count',
        description: t('NUMBER_OF_CRASHES'),
        colour: '#0fa336',
        visible: true
      }]}
      export={{ filename, onExportClicked, csv: true }}
      hasLegend
      useAxisTooltip
      bottomBorder
      formatTooltipDate={formatTooltipDay}
      drawXAxis={(chart, xAxis) => {
        chartRef.current = chart;
        drawXAxisLabelsFromDateRange(xAxis as am4charts.DateAxis, dateRange);
      }}
      onBarClick={onBarClick}
    />
  );
}
